import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { backupApi } from '../services/api';
import { BackupStatus, BackupArchive } from '../types';
import { SectionCard, useToast } from '@vmcom/ui';

const BackupSettings: React.FC = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [archives, setArchives] = useState<BackupArchive[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(true);
  const [hour, setHour] = useState(3);
  const [minute, setMinute] = useState(30);
  const [keepCount, setKeepCount] = useState(14);
  const [includeMirrors, setIncludeMirrors] = useState(false);

  const [restoreFile, setRestoreFile] = useState<string | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [deleteFile, setDeleteFile] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [st, list] = await Promise.all([backupApi.getStatus(), backupApi.list()]);
      setStatus(st);
      setArchives(list);
      setEnabled(st.settings.enabled);
      setHour(st.settings.hour);
      setMinute(st.settings.minute);
      setKeepCount(st.settings.keep_count);
      setIncludeMirrors(st.settings.include_mirrors);
    } catch {
      toast('Не удалось загрузить состояние резервного копирования', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    try {
      setCreating(true);
      toast('Архив создаётся, это может занять несколько минут', 'info');
      const res = await backupApi.create();
      if (res.success) {
        toast(`Архив создан: ${res.data.size || ''}`, 'success');
      } else {
        toast(res.data?.error || 'Не удалось создать архив', 'error');
      }
      await load();
    } catch {
      toast('Не удалось создать архив', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await backupApi.saveSettings({
        enabled,
        hour,
        minute,
        keep_count: keepCount,
        include_mirrors: includeMirrors,
      });
      toast('Настройки сохранены', 'success');
      await load();
    } catch {
      toast('Не удалось сохранить настройки', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFile) return;
    try {
      await backupApi.remove(deleteFile);
      toast('Архив удалён', 'success');
      setDeleteFile(null);
      await load();
    } catch {
      toast('Не удалось удалить архив', 'error');
    }
  };

  const handleDownload = async (file: string) => {
    try {
      setDownloadingFile(file);
      await backupApi.download(file);
      toast('Загрузка началась', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось скачать архив', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    try {
      setRestoring(true);
      await backupApi.restore(restoreFile, restoreConfirm);
      toast('Данные восстановлены. Перезапустите backend и выполните синхронизацию Redis.', 'success');
      setRestoreFile(null);
      setRestoreConfirm('');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast(err?.response?.data?.detail || 'Восстановление не удалось', 'error');
    } finally {
      setRestoring(false);
    }
  };

  const last = status?.last;
  const lastOk = last?.success;
  const pagedArchives = archives.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const statBox = (title: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {title}
      </Typography>
      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={2}>
      <SectionCard
        title="Состояние"
        action={
          <Button size="small" startIcon={<RefreshOutlinedIcon />} disabled={loading} onClick={load}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Обновить
          </Button>
        }
      >
        {!last && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Резервных копий ещё не было. Создайте первый архив кнопкой ниже или дождитесь запуска по расписанию.
          </Alert>
        )}
        {last && !lastOk && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Последний архив не создался: {last.error || 'Причина неизвестна — смотрите логи backend'}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {statBox('Последний архив', last ? new Date(last.started_at).toLocaleString('ru-RU') : '—')}
          </Grid>
          <Grid item xs={12} md={2}>
            {statBox(
              'Результат',
              last ? (
                <Chip size="small" label={lastOk ? 'успешно' : 'ошибка'} color={lastOk ? 'success' : 'error'} />
              ) : (
                <Chip size="small" label="нет данных" />
              ),
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            {statBox('Размер', last?.size || '—')}
          </Grid>
          <Grid item xs={12} md={2}>
            {statBox('Архивов на диске', status ? `${status.archives_count} (${status.archives_total_size})` : '—')}
          </Grid>
          <Grid item xs={12} md={3}>
            {statBox('Свободно на диске', status?.disk_free || '—')}
          </Grid>
        </Grid>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Архивы лежат на сервере в{' '}
          <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
            {status?.directory || 'mongodb-backups'}
          </Typography>
          . Кэш Redis не архивируется — он полностью перестраивается из MongoDB и 1С.
        </Typography>

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          startIcon={<PlayCircleOutlineIcon />}
          disabled={creating}
          onClick={handleCreate}
        >
          {creating ? 'Создание…' : 'Создать архив сейчас'}
        </Button>
      </SectionCard>

      <SectionCard title="Расписание">
        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" gap={3} alignItems="center">
            <FormControlLabel control={<Switch checked={enabled} onChange={(_, v) => setEnabled(v)} />} label="Создавать автоматически" />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Время:</Typography>
              <TextField
                type="number"
                size="small"
                inputProps={{ min: 0, max: 23 }}
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                sx={{ width: 70 }}
              />
              <Typography>:</Typography>
              <TextField
                type="number"
                size="small"
                inputProps={{ min: 0, max: 59 }}
                value={minute}
                onChange={(e) => setMinute(Number(e.target.value))}
                sx={{ width: 70 }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Хранить копий:</Typography>
              <TextField
                type="number"
                size="small"
                inputProps={{ min: 1, max: 365 }}
                value={keepCount}
                onChange={(e) => setKeepCount(Number(e.target.value))}
                sx={{ width: 90 }}
              />
            </Stack>
          </Stack>

          <FormControlLabel
            control={<Switch checked={includeMirrors} onChange={(_, v) => setIncludeMirrors(v)} />}
            label={
              <Box>
                <Typography variant="body2">Включать зеркала 1С (товары, ветки, категории, бренды)</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Выключено: архив ~30 МБ, после восстановления зеркала собираются синхронизацией. Включено: архив ~730 МБ,
                  зато восстанавливается без обращения к 1С.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />

          <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={saving} onClick={handleSave}>
            Сохранить настройки
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard title="Архивы">
        {loading && !archives.length ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Создан</TableCell>
                    <TableCell width={110}>Размер</TableCell>
                    <TableCell>Файл</TableCell>
                    <TableCell width={240} align="right">
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedArchives.map((r) => (
                    <TableRow key={r.file} hover>
                      <TableCell>{new Date(r.created_at).toLocaleString('ru-RU')}</TableCell>
                      <TableCell>{r.size}</TableCell>
                      <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.file}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={
                              downloadingFile === r.file ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CloudDownloadOutlinedIcon />
                              )
                            }
                            onClick={() => handleDownload(r.file)}
                            disabled={downloadingFile === r.file}
                          >
                            Скачать
                          </Button>
                          <Button size="small" startIcon={<UndoOutlinedIcon />} color="warning" onClick={() => setRestoreFile(r.file)}>
                            Восстановить
                          </Button>
                          <IconButton size="small" color="error" onClick={() => setDeleteFile(r.file)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {archives.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={archives.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25]}
                labelRowsPerPage="Строк:"
              />
            )}
          </>
        )}
      </SectionCard>

      <Dialog open={!!restoreFile} onClose={() => { setRestoreFile(null); setRestoreConfirm(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Восстановление из архива</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Текущие данные будут перезаписаны. Все коллекции из архива{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              {restoreFile}
            </Typography>{' '}
            заменят текущие. После восстановления перезапустите backend и выполните полную синхронизацию Redis — кэш
            останется от прежних данных.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Для подтверждения введите <strong>ВОССТАНОВИТЬ</strong>:
          </Typography>
          <TextField
            fullWidth
            value={restoreConfirm}
            onChange={(e) => setRestoreConfirm(e.target.value)}
            placeholder="ВОССТАНОВИТЬ"
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRestoreFile(null); setRestoreConfirm(''); }}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            disabled={restoreConfirm !== 'ВОССТАНОВИТЬ' || restoring}
            onClick={handleRestore}
          >
            Восстановить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteFile} onClose={() => setDeleteFile(null)}>
        <DialogTitle>Удалить архив?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{deleteFile}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFile(null)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default BackupSettings;
